import { useQueryClient } from "@tanstack/react-query";
import { Button, Field } from "@secretaa/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api } from "../../app/api.ts";
import { errorMessage } from "../../shared/errors.ts";
import { Form, FormError } from "../../shared/form.tsx";
import { safeNext, takeReturnTo, withNext } from "../../shared/return-to.ts";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function signIn(loginEmail: string, loginPassword: string) {
    setBusy(true);
    setError(null);
    try {
      await api.csrf();
      await api.auth.login(loginEmail, loginPassword);
      await qc.invalidateQueries();
      const remembered = takeReturnTo();
      navigate(next ?? remembered ?? "/select-workspace");
    } catch (e) {
      setError(errorMessage(t, e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className="mb-4 text-xl font-semibold">{t("auth.login.title")}</h1>
      <Form
        label={t("auth.login.title")}
        onSubmit={() => signIn(email, password)}
      >
        <FormError message={error} />
        <Field label={t("auth.fields.email")} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label={t("auth.fields.password")} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" disabled={busy}>
          {busy ? t("common.saving") : t("auth.login.submit")}
        </Button>
        {import.meta.env.DEV ? (
          <Button type="button" disabled={busy} onClick={() => signIn("demo@secretaa.local", "demo-password-123")}>
            {t("auth.login.devDemo")}
          </Button>
        ) : null}
      </Form>
      <div className="mt-4 flex justify-between text-sm">
        <Link className="underline" to="/forgot-password">
          {t("auth.login.forgot")}
        </Link>
        <span>
          {t("auth.login.noAccount")}{" "}
          <Link className="underline" to={withNext("/register", next, email ? { email } : {})}>
            {t("auth.login.register")}
          </Link>
        </span>
      </div>
    </>
  );
}
